import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import SignatureAuditLogs from '../components/SignatureAuditLogs';

interface TenantData {
  id: string;
  name: string;
  domain: string;
  azureTenantId: string;
  licenseStatus: string;
  isActive: boolean;
  trialStartDate: string | null;
  trialEndDate: string | null;
  subscriptionStart: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  maxLicenses: number;
  maxLogoUploadSizeMB: number;
  createdAt: string;
  _count: {
    users: number;
    groups: number;
    templates: number;
    signatureDeployments: number;
  };
  stats: {
    licensedUserCount: number;
  };
  recentUsers: Array<{
    id: string;
    email: string;
    displayName: string;
    isLicensed: boolean;
    createdAt: string;
  }>;
}

export default function TenantDetail({ adminUser }: any) {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [usersPagination, setUsersPagination] = useState<any>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showExtendTrialModal, setShowExtendTrialModal] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [newLicenseCount, setNewLicenseCount] = useState(0);
  const [licenseReason, setLicenseReason] = useState('');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [newSubEnd, setNewSubEnd] = useState('');
  const [subReason, setSubReason] = useState('');
  const [showFreeModal, setShowFreeModal] = useState(false);
  const [freeReason, setFreeReason] = useState('');
  const [showLogoSizeModal, setShowLogoSizeModal] = useState(false);
  const [newLogoSize, setNewLogoSize] = useState(1);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [testEmailType, setTestEmailType] = useState<'limit_exceeded' | 'grace_expiring'>('limit_exceeded');

  useEffect(() => {
    if (tenantId) {
      fetchTenantDetail();
      fetchUsers();
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      fetchUsers();
    }
  }, [usersPage, usersSearch]);

  const fetchTenantDetail = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setTenant(data.data);
      } else {
        console.error('Failed to fetch tenant');
      }
    } catch (error) {
      console.error('Error fetching tenant:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const params = new URLSearchParams({
        page: usersPage.toString(),
        limit: '20',
      });
      if (usersSearch) params.append('search', usersSearch);
      
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/users?${params.toString()}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data.users);
        setUsersPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleExtendTrial = async () => {
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/extend-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ days: extendDays }),
      });
      if (response.ok) {
        toast.success('Trial extended successfully!');
        fetchTenantDetail();
        setShowExtendTrialModal(false);
      } else {
        toast.error('Failed to extend trial');
      }
    } catch (error) {
      toast.error('Error extending trial');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmSuspend = async () => {
    setShowSuspendModal(false);
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/suspend`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Tenant suspended successfully');
        fetchTenantDetail();
      } else {
        toast.error('Failed to suspend tenant');
      }
    } catch (error) {
      toast.error('Error suspending tenant');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmReactivate = async () => {
    setShowReactivateModal(false);
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Tenant reactivated successfully');
        fetchTenantDetail();
      } else {
        toast.error('Failed to reactivate tenant');
      }
    } catch (error) {
      toast.error('Error reactivating tenant');
    } finally {
      setActionLoading(false);
    }
  };

  const handleImpersonate = async () => {
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/impersonate`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        window.open(data.data.redirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        toast.error(data.error || 'Failed to start impersonation session');
      }
    } catch (error) {
      toast.error('Error starting impersonation session');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncUsers = async () => {
    const loadingToast = toast.loading('Queuing user sync...');
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/sync-users`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('User sync job queued - worker will process shortly', { id: loadingToast });
      } else {
        toast.error('Failed to queue user sync', { id: loadingToast });
      }
    } catch (error) {
      toast.error('Error queuing user sync', { id: loadingToast });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncGroups = async () => {
    const loadingToast = toast.loading('Queuing group sync...');
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/sync-groups`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Group sync job queued - worker will process shortly', { id: loadingToast });
      } else {
        toast.error('Failed to queue group sync', { id: loadingToast });
      }
    } catch (error) {
      toast.error('Error queuing group sync', { id: loadingToast });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateLicense = async () => {
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/update-license-count`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ licenseCount: newLicenseCount, reason: licenseReason }),
      });
      if (response.ok) {
        toast.success('License count updated successfully');
        fetchTenantDetail();
        setShowLicenseModal(false);
        setLicenseReason('');
      } else {
        toast.error('Failed to update license count');
      }
    } catch (error) {
      toast.error('Error updating license count');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendSubscription = async () => {
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/extend-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ endDate: newSubEnd, reason: subReason }),
      });
      if (response.ok) {
        toast.success('Subscription end date updated successfully');
        fetchTenantDetail();
        setShowSubscriptionModal(false);
        setSubReason('');
      } else {
        toast.error('Failed to update subscription');
      }
    } catch (error) {
      toast.error('Error updating subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetFree = async () => {
    if (!freeReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/set-free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: freeReason }),
      });
      if (response.ok) {
        toast.success('Tenant set to free tier successfully');
        fetchTenantDetail();
        setShowFreeModal(false);
        setFreeReason('');
      } else {
        toast.error('Failed to set free tier');
      }
    } catch (error) {
      toast.error('Error setting free tier');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateLogoSize = async () => {
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/update-logo-size`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sizeMB: newLogoSize }),
      });
      if (response.ok) {
        toast.success('Logo upload size limit updated successfully');
        fetchTenantDetail();
        setShowLogoSizeModal(false);
      } else {
        toast.error('Failed to update logo size limit');
      }
    } catch (error) {
      toast.error('Error updating logo size limit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailRecipient || !testEmailRecipient.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    const loadingToast = toast.loading('Sending test email...');
    setActionLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/tenants/${tenantId}/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ recipientEmail: testEmailRecipient, emailType: testEmailType }),
      });
      if (response.ok) {
        toast.success(`Test email sent to ${testEmailRecipient}`, { id: loadingToast });
        setShowTestEmailModal(false);
        setTestEmailRecipient('');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to send test email', { id: loadingToast });
      }
    } catch (error: any) {
      toast.error(error.message || 'Error sending test email', { id: loadingToast });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return { bg: '#d1fae5', color: '#065f46' };
      case 'TRIAL': return { bg: '#fef3c7', color: '#92400e' };
      case 'EXPIRED': return { bg: '#fee2e2', color: '#991b1b' };
      case 'SUSPENDED': return { bg: '#e5e7eb', color: '#374151' };
      default: return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  if (loading) {
    return (
      <>
        <Navigation adminUser={adminUser} />
        <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ color: '#666' }}>Loading tenant details...</div>
        </div>
      </>
    );
  }

  if (!tenant) {
    return (
      <>
        <Navigation adminUser={adminUser} />
        <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ color: '#ef4444' }}>Tenant not found</div>
        </div>
      </>
    );
  }

  const statusColors = getStatusColor(tenant.licenseStatus);

  return (
    <>
      <Navigation adminUser={adminUser} />
      <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', background: '#f9fafb', minHeight: 'calc(100vh - 64px)' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/tenants')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#3b82f6',
              fontSize: '14px',
              cursor: 'pointer',
              marginBottom: '16px',
              padding: '4px 0',
            }}
          >
            ← Back to Tenants
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
              {tenant.name}
            </h1>
            <span style={{
              padding: '6px 16px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              background: statusColors.bg,
              color: statusColors.color,
            }}>
              {tenant.licenseStatus}
            </span>
          </div>
          <p style={{ color: '#6b7280', margin: 0 }}>{tenant.domain}</p>
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Total Users</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{tenant._count.users}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Licensed Users</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{tenant.stats.licensedUserCount}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Groups</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{tenant._count.groups}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Templates</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{tenant._count.templates}</div>
          </div>
        </div>

        {/* Actions Section */}
        <div style={{ ...cardStyle, marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Actions</h2>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {tenant.licenseStatus === 'TRIAL' && (
              <button
                onClick={() => setShowExtendTrialModal(true)}
                disabled={actionLoading}
                style={{
                  padding: '10px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                Extend Trial
              </button>
            )}
            {tenant.licenseStatus !== 'SUSPENDED' && tenant.isActive && (
              <button
                onClick={() => setShowSuspendModal(true)}
                disabled={actionLoading}
                style={{
                  padding: '10px 16px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                Suspend Tenant
              </button>
            )}
            {(tenant.licenseStatus === 'SUSPENDED' || !tenant.isActive) && (
              <button
                onClick={() => setShowReactivateModal(true)}
                disabled={actionLoading}
                style={{
                  padding: '10px 16px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                Reactivate Tenant
              </button>
            )}
            <button
              onClick={handleSyncUsers}
              disabled={actionLoading}
              style={{
                padding: '10px 16px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Sync Users
            </button>
            <button
              onClick={handleSyncGroups}
              disabled={actionLoading}
              style={{
                padding: '10px 16px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Sync Groups
            </button>
            <button
              onClick={handleImpersonate}
              disabled={actionLoading}
              title="Opens the customer's dashboard as their admin, in a new tab, for up to 30 minutes"
              style={{
                padding: '10px 16px',
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Manage Signatures &amp; Templates
            </button>
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginTop: '24px', marginBottom: '12px' }}>License Management</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => { setNewLicenseCount(tenant.maxLicenses); setShowLicenseModal(true); }}
              disabled={actionLoading}
              style={{
                padding: '10px 16px',
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Update License Count ({tenant.maxLicenses})
            </button>
            <button
              onClick={() => setShowSubscriptionModal(true)}
              disabled={actionLoading}
              style={{
                padding: '10px 16px',
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Extend Subscription
            </button>
            <button
              onClick={() => setShowFreeModal(true)}
              disabled={actionLoading}
              style={{
                padding: '10px 16px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Set as Free Tier
            </button>
            <button
              onClick={() => { setNewLogoSize(tenant.maxLogoUploadSizeMB || 1); setShowLogoSizeModal(true); }}
              disabled={actionLoading}
              style={{
                padding: '10px 16px',
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              Logo Upload Limit ({tenant.maxLogoUploadSizeMB || 1}MB)
            </button>
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginTop: '24px', marginBottom: '12px' }}>Email Testing</h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowTestEmailModal(true)}
              disabled={actionLoading}
              style={{
                padding: '10px 16px',
                background: '#0ea5e9',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              📧 Send Test Email
            </button>
          </div>
        </div>

        {/* Extend Trial Modal */}
        {showExtendTrialModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '400px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Extend Trial Period</h3>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
                Extend by (days):
              </label>
              <input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                min="1"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '20px',
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowExtendTrialModal(false)}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendTrial}
                  disabled={actionLoading || extendDays < 1}
                  style={{
                    padding: '10px 16px',
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: (actionLoading || extendDays < 1) ? 'not-allowed' : 'pointer',
                    opacity: (actionLoading || extendDays < 1) ? 0.6 : 1,
                  }}
                >
                  {actionLoading ? 'Extending...' : 'Extend Trial'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Suspend Confirmation Modal */}
        {showSuspendModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '400px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#ef4444' }}>⚠️ Suspend Tenant</h3>
              <p style={{ marginBottom: '20px', color: '#374151' }}>
                Are you sure you want to suspend <strong>{tenant?.name}</strong>? They will lose access immediately.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowSuspendModal(false)}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSuspend}
                  style={{
                    padding: '10px 16px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Suspend Tenant
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reactivate Confirmation Modal */}
        {showReactivateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '400px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#10b981' }}>Reactivate Tenant</h3>
              <p style={{ marginBottom: '20px', color: '#374151' }}>
                Reactivate <strong>{tenant?.name}</strong>? They will regain access to the system.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowReactivateModal(false)}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReactivate}
                  style={{
                    padding: '10px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Reactivate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
          {/* Company Information */}
          <div style={cardStyle}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Company Information</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Domain</div>
                <div style={{ color: '#111827' }}>{tenant.domain}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Azure Tenant ID</div>
                <div style={{ color: '#111827', fontSize: '14px', fontFamily: 'monospace' }}>{tenant.azureTenantId}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Status</div>
                <div style={{ color: tenant.isActive ? '#10b981' : '#ef4444', fontWeight: '500' }}>
                  {tenant.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Created</div>
                <div style={{ color: '#111827' }}>{formatDate(tenant.createdAt)}</div>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div style={cardStyle}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Subscription Details</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>License Status</div>
                <div style={{ color: '#111827' }}>{tenant.licenseStatus}</div>
              </div>
              {tenant.licenseStatus === 'TRIAL' && (
                <>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Trial Start</div>
                    <div style={{ color: '#111827' }}>{formatDate(tenant.trialStartDate)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Trial End</div>
                    <div style={{ color: '#111827' }}>{formatDate(tenant.trialEndDate)}</div>
                  </div>
                </>
              )}
              {tenant.licenseStatus === 'ACTIVE' && (
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Subscription Start</div>
                  <div style={{ color: '#111827' }}>{formatDate(tenant.subscriptionStart)}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Max Licenses</div>
                <div style={{ color: '#111827' }}>{tenant.maxLicenses}</div>
              </div>
              {tenant.stripeCustomerId && (
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>Stripe Customer</div>
                  <div style={{ color: '#111827', fontSize: '14px', fontFamily: 'monospace' }}>{tenant.stripeCustomerId}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Users Section */}
        <div style={{ ...cardStyle, marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Users ({usersPagination?.total || 0})</h2>
            <input
              type="text"
              placeholder="Search users..."
              value={usersSearch}
              onChange={(e) => {
                setUsersSearch(e.target.value);
                setUsersPage(1);
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '250px',
              }}
            />
          </div>
          
          {loadingUsers ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading users...</div>
          ) : users.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No users found</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Name</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Email</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Job Title</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Department</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Licensed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user: any) => (
                      <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 8px', color: '#111827' }}>{user.displayName}</td>
                        <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '14px' }}>{user.email}</td>
                        <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '14px' }}>{user.jobTitle || '-'}</td>
                        <td style={{ padding: '12px 8px', color: '#6b7280', fontSize: '14px' }}>{user.department || '-'}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            background: user.isLicensed ? '#d1fae5' : '#f3f4f6',
                            color: user.isLicensed ? '#065f46' : '#6b7280',
                          }}>
                            {user.isLicensed ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {usersPagination && usersPagination.totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                  <button
                    onClick={() => setUsersPage(Math.max(1, usersPage - 1))}
                    disabled={usersPage === 1}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: usersPage === 1 ? '#f3f4f6' : 'white',
                      color: usersPage === 1 ? '#9ca3af' : '#374151',
                      cursor: usersPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>
                    Page {usersPage} of {usersPagination.totalPages}
                  </span>
                  <button
                    onClick={() => setUsersPage(Math.min(usersPagination.totalPages, usersPage + 1))}
                    disabled={usersPage === usersPagination.totalPages}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: usersPage === usersPagination.totalPages ? '#f3f4f6' : 'white',
                      color: usersPage === usersPagination.totalPages ? '#9ca3af' : '#374151',
                      cursor: usersPage === usersPagination.totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Signature Audit Logs Section */}
        {tenantId && <SignatureAuditLogs tenantId={tenantId} />}

        {/* License Count Modal */}
        {showLicenseModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '450px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#8b5cf6' }}>Update License Count</h3>
              <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                Current license count: <strong>{tenant?.maxLicenses}</strong>
              </p>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                New License Count:
              </label>
              <input
                type="number"
                value={newLicenseCount}
                onChange={(e) => setNewLicenseCount(parseInt(e.target.value) || 0)}
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '16px',
                }}
              />
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                Reason (optional):
              </label>
              <textarea
                value={licenseReason}
                onChange={(e) => setLicenseReason(e.target.value)}
                placeholder="e.g., Sponsored org, custom agreement"
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '20px',
                  resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowLicenseModal(false); setLicenseReason(''); }}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateLicense}
                  disabled={actionLoading || newLicenseCount < 0}
                  style={{
                    padding: '10px 16px',
                    background: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: (actionLoading || newLicenseCount < 0) ? 'not-allowed' : 'pointer',
                    opacity: (actionLoading || newLicenseCount < 0) ? 0.6 : 1,
                  }}
                >
                  {actionLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Extension Modal */}
        {showSubscriptionModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '450px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#8b5cf6' }}>Extend Subscription</h3>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                New Billing Date:
              </label>
              <input
                type="date"
                value={newSubEnd}
                onChange={(e) => setNewSubEnd(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '16px',
                }}
              />
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                Reason (optional):
              </label>
              <textarea
                value={subReason}
                onChange={(e) => setSubReason(e.target.value)}
                placeholder="e.g., Sponsored extension, special agreement"
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '20px',
                  resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowSubscriptionModal(false); setSubReason(''); setNewSubEnd(''); }}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendSubscription}
                  disabled={actionLoading || !newSubEnd}
                  style={{
                    padding: '10px 16px',
                    background: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: (actionLoading || !newSubEnd) ? 'not-allowed' : 'pointer',
                    opacity: (actionLoading || !newSubEnd) ? 0.6 : 1,
                  }}
                >
                  {actionLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Free Tier Modal */}
        {showFreeModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '450px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#10b981' }}>Set as Free Tier</h3>
              <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                This will set <strong>{tenant?.name}</strong> to free tier with:
              </p>
              <ul style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px', paddingLeft: '20px' }}>
                <li>License Status: <strong>ACTIVE</strong></li>
                <li>Max Licenses: <strong>999,999</strong> (unlimited)</li>
                <li>Next Billing Date: <strong>2099-12-31</strong> (effectively no expiration)</li>
                <li>Stripe IDs will be cleared</li>
              </ul>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                Reason (required):
              </label>
              <textarea
                value={freeReason}
                onChange={(e) => setFreeReason(e.target.value)}
                placeholder="e.g., Internal use, Sponsored non-profit, Developer"
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '20px',
                  resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowFreeModal(false); setFreeReason(''); }}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSetFree}
                  disabled={actionLoading || !freeReason.trim()}
                  style={{
                    padding: '10px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: (actionLoading || !freeReason.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (actionLoading || !freeReason.trim()) ? 0.6 : 1,
                  }}
                >
                  {actionLoading ? 'Setting...' : 'Set as Free'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logo Upload Size Modal */}
        {showLogoSizeModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '450px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#f59e0b' }}>Update Logo Upload Size Limit</h3>
              <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                Current limit: <strong>{tenant?.maxLogoUploadSizeMB || 1}MB</strong>
              </p>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                New Size Limit (MB):
              </label>
              <input
                type="number"
                value={newLogoSize}
                onChange={(e) => setNewLogoSize(parseInt(e.target.value) || 1)}
                min="1"
                max="50"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '20px' }}>
                Range: 1-50 MB. Recommended: 1-5 MB for most use cases.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowLogoSizeModal(false)}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateLogoSize}
                  disabled={actionLoading || newLogoSize < 1 || newLogoSize > 50}
                  style={{
                    padding: '10px 16px',
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: (actionLoading || newLogoSize < 1 || newLogoSize > 50) ? 'not-allowed' : 'pointer',
                    opacity: (actionLoading || newLogoSize < 1 || newLogoSize > 50) ? 0.6 : 1,
                  }}
                >
                  {actionLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Test Email Modal */}
        {showTestEmailModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '450px',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#0ea5e9' }}>📧 Send Test Email</h3>
              <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                Send a test license limit notification email to preview the email templates.
              </p>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                Recipient Email:
              </label>
              <input
                type="email"
                value={testEmailRecipient}
                onChange={(e) => setTestEmailRecipient(e.target.value)}
                placeholder="admin@example.com"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '16px',
                }}
              />
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                Email Type:
              </label>
              <select
                value={testEmailType}
                onChange={(e) => setTestEmailType(e.target.value as 'limit_exceeded' | 'grace_expiring')}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '8px',
                }}
              >
                <option value="limit_exceeded">License Limit Exceeded</option>
                <option value="grace_expiring">Grace Period Expiring (3 days)</option>
              </select>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '20px' }}>
                Mock data will be used: {tenant?.maxLicenses + 5} users (5 over limit of {tenant?.maxLicenses})
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowTestEmailModal(false); setTestEmailRecipient(''); }}
                  style={{
                    padding: '10px 16px',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendTestEmail}
                  disabled={actionLoading || !testEmailRecipient || !testEmailRecipient.includes('@')}
                  style={{
                    padding: '10px 16px',
                    background: '#0ea5e9',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: (actionLoading || !testEmailRecipient || !testEmailRecipient.includes('@')) ? 'not-allowed' : 'pointer',
                    opacity: (actionLoading || !testEmailRecipient || !testEmailRecipient.includes('@')) ? 0.6 : 1,
                  }}
                >
                  {actionLoading ? 'Sending...' : 'Send Test Email'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
